import React from 'react';
import _ from 'lodash';
import cx from 'classnames';

import { yup } from 'utils/forms';

import { ButtonList } from 'components/Button'


export const Form = ({ className, onSubmit, ...props }) => {
  let onSubmitForm = e => {
    e.preventDefault(); //Cancel page post
    onSubmit?.(e);
  }
  return <form className={cx("g-form", className)} onSubmit={onSubmitForm} {...props} />;
}
export default Form;

export const FormSmall = ({ className, ...props }) => <Form className="s" {...props} />


export const FormSection = ({ title, children, className, ...props }) => (
  <div className={cx("g-form-section", className)} {...props}>
    {title &&
      <SectionTitle>{title}</SectionTitle>}
    <SectionBody>
      {children}
    </SectionBody>
  </div>)

export const SectionHead = ({ className, ...props }) => (
  <div className={cx("section-head", className)} {...props} />)

export const SectionTitle = ({ className, ...props }) => (
  <div className={cx("section-title", className)} {...props} />)

export const SectionBody = ({ className, ...props }) => (
  <div className={cx("section-body", className)} {...props} />)

export const SectionFooter = ({ className, ...props }) => (
  <div className={cx("section-footer", className)} {...props} />)

export const SectionActions = ({ ...props }) => (
  <ButtonList className="section-actions" {...props} />
)


export const FormItem = ({ className, align, ...props }) => (
  <div className={cx("g-form-item", align, className)} {...props} />)

export const FormItemSplit = ({ className, ...props }) => (
  <FormItem {...props} align="split" />)

export const ItemLabel = ({ className, ...props }) => (
  <div className={cx("item-label", className)} {...props} />)

export const ItemInput = ({ className, ...props }) => (
  <div className={cx("item-input", className)} {...props} />)
















// VALIDATES USING YUP
// https://github.com/jquense/yup#mixeddescribe-schemadescription for API
export const withForm = Component => {
  return class extends React.Component {

    state = {
      schema: null,
      values: {},
      edits: {},
      errors: {},
      files: {},
    }

    _transformIn = (value, name) => this.state.schema?.[name]?.transformIn?.(value) ?? value
    _transformOut = (value, name) => this.state.schema?.[name]?.transformOut?.(value) ?? value

    // 
    // Set the schema object for validation and input handling
    setSchema = async schema => {
      return new Promise((resolve, reject) => {
        this.setState({
          schema,
          values: _.mapValues(schema, s => s.default ? s.default : null),
          edits: _.mapValues(schema, _.constant(false)),
          errors: {},
        }, () => resolve())
      })
    }

    // 
    // Sets the values, and edited states to false, and removes any errors
    // Usually this would be used when an async API returned and you want to set the initial state
    setDefaults = values => {
      return this.setState((prevState) => ({
      values: { 
        ...prevState.values, 
        ..._.mapValues(values, (v, k) => this._transformIn(v, k))
      },
      edits: { 
        ...prevState.edits, 
        ..._.mapValues(values, _.constant(false))
      },
      errors: {},
    }))
  }

    setErrors = (name, error) => {
      if(_.isPlainObject(name))
        return this.setState({ errors: { ...this.state.errors, ...name } })
      this.setState({ errors: { ...this.state.errors, [name]: error } })
    }

    setValues = (name, value) => {
      let values = name;
      if(!_.isPlainObject(name)) {
        values = {
          [name]: value,
        }
      }
      return this.setState((prevState) => ({
        values: { 
          ...prevState.values, 
          ..._.mapValues(values, (v, k) => this._transformIn(v, k))
        },
        edits: {
          ...prevState.edits,
          ..._.mapValues(values, _.constant(true)),
        } 
      }));
    }

    hasChanges = () => _.some(this.state.edits)
    editedValues = () => this.state.values

    //
    // <Input> component onChange handler. Give this handler to all your inputs
    onInputChange = (value, name, e, file) => {
      let { errors } = this.state;
      this.setState({
        values: {
          ...this.state.values,
          [name]: value
        },
        edits: {
          ...this.state.edits,
          [name]: this.state.values[name] !== value
        },
        errors: _.omit(this.state.errors, name),
      })
      if(file)
        this.setState({
          files: {
            ...this.state.files,
            [name]: file
          }
        });
    }

    //
    // Runs validate function from the schema, on every value that has been edited
    validateForm = async () => {
      let { values, edits, schema } = this.state;
      let editedValues = _.pick(values, _.pickBy(edits, v => v === true) |> _.keys(#));
      let validateSchema = schema
        |> _.pick(#, _.keys(values))
        |> _.omit(#, v => _.isNil(v.validate))
        |> _.mapValues(#, 'validate')
        |> _.mapValues(#, (ys, key) => schema[key].name ? ys.label(schema[key].name) : ys)
        |> yup.object().shape(#)
      let errors = [];

      return new Promise(async (resolve, reject) => {
        try {
          let results = await validateSchema.validate(values, {
            abortEarly: false,
            stripUnknown: false
          });
          this.setState({ values: results }, 
            s => resolve(this.transformValues()));
        } catch(err) {
          this.setState({
            errors: {
              ...(err.inner
                |> _.keyBy(#, 'path')
                |> _.mapValues(#, 'message'))
            }
          })
          resolve(null);
        }
      })
    }

    transformValues = () => _.mapValues(this.state.values, (v, k) => 
      this.state.schema?.[k]?.transformOut 
        ? this.state.schema[k].transformOut(v) 
        : v)



    //
    // Resets the edit state. Usually after a form was submitted
    resetForm = () => this.setState({ edits: _.mapValues(this.state.edits, _.constant(false)) })

    render() {
      return (
        <Component
          validateForm={this.validateForm.bind(this)}
          validate={this.validateForm.bind(this)}
          resetForm={this.resetForm.bind(this)}
          onInputChange={this.onInputChange.bind(this)}
          setSchema={this.setSchema.bind(this)}
          setDefaults={this.setDefaults.bind(this)}
          setValues={this.setValues.bind(this)}
          setErrors={this.setErrors.bind(this)}
          hasChanges={this.hasChanges.bind(this)}
          hasEdits={this.hasChanges.bind(this)}
          editedValues={this.editedValues}
          values={this.state.values}
          edits={this.state.edits}
          errors={this.state.errors}
          {...this.props} />)
    }
  }
}





export class FormComponent extends React.Component {
  constructor(props, schema) {
    super(props);
    let names = _.keys(schema);
    this.state = {
      schema,
      values: _.reduce(names, (acc, key) => _.set(acc, key, schema[key]?.default ?? ''), {}),
      errors: _.reduce(names, (acc, key) => _.set(acc, key, null), {}),
      edits: _.reduce(names, (acc, key) => _.set(acc, key, false), {}),
      saving: false,
    }
  }

  validate = values => {
    let { schema } = this.state;
    return schema.validate(values, { abortEarly: false });
  }

  transform = values => {
    let { transforms } = this.state;
    if(!transforms)
      return values;
    return _.mapValues(values, (v,k) => {
      if(_.isFunction(transforms[k])) {
        return transforms[k](v);
      }
      return v;
    })
  }

  onBlurValidate = e => {
    let { name, value } = e.target;
    let { schema } = this.state;
    // TODO
  }

  onInputChange = (value, name) => {
    this.setState({
      values: { ...this.state.values, [name]: value },
      edits: { ...this.state.edits, [name]: true }
    });
  }

}

